import { useState, useRef, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pen, Type, RotateCcw, Loader2 } from 'lucide-react';

interface SignatureCaptureProps {
  role: string;
  signerName: string;
  onSave: (signatureData: string) => void;
  onCancel: () => void;
  isLoading?: boolean;
  primaryOfficerRole?: string;
}

// Map common role slugs to display labels
const getRoleLabel = (role: string, primaryOfficerRole?: string): string => {
  // If this is the treasury_secretary role and we have a custom primary officer role, use it
  if (role === 'treasury_secretary' && primaryOfficerRole) {
    return primaryOfficerRole;
  }
  
  const staticLabels: Record<string, string> = {
    recording_secretary: 'Recording Secretary',
    president: 'President',
    vice_president: 'Vice President',
    treasury_secretary: 'Secretary Treasurer',
    secretary: 'Secretary',
    managing_member: 'Managing Member',
    board_member: 'Board Member',
  };
  
  // Return static label if found, otherwise format the role string nicely
  return staticLabels[role] || role.split('_').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};

export function SignatureCapture({
  role,
  signerName,
  onSave,
  onCancel,
  isLoading = false,
  primaryOfficerRole,
}: SignatureCaptureProps) {
  const sigCanvas = useRef<SignatureCanvas>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [method, setMethod] = useState<'draw' | 'type'>('draw');
  const [typedName, setTypedName] = useState(signerName);
  const [isEmpty, setIsEmpty] = useState(true);
  const [canvasSize, setCanvasSize] = useState({ width: 600, height: 200 });

  const roleLabel = getRoleLabel(role, primaryOfficerRole);

  // Responsive canvas sizing
  useEffect(() => {
    const updateCanvasSize = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        // Cap at 600px max, use container width minus padding on mobile
        const width = Math.min(containerWidth - 4, 600);
        const height = Math.round(width * 0.33); // Maintain aspect ratio
        setCanvasSize({ width, height });
      }
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, []);

  const handleClear = () => {
    sigCanvas.current?.clear();
    setIsEmpty(true);
  };

  const handleDrawEnd = () => {
    setIsEmpty(sigCanvas.current?.isEmpty() ?? true);
  };

  const handleSave = () => {
    let signatureData: string;

    if (method === 'draw') {
      const canvas = sigCanvas.current?.getCanvas();
      if (!canvas || isEmpty) return;
      signatureData = canvas.toDataURL('image/png');
    } else {
      // Generate signature from typed name
      signatureData = generateTypedSignature(typedName);
    }

    onSave(signatureData);
  };

  const generateTypedSignature = (name: string): string => {
    // Create canvas for typed signature
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 100;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) return '';

    // Style as signature
    ctx.fillStyle = '#000000';
    ctx.font = '48px "Brush Script MT", cursive';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(name, 200, 50);

    return canvas.toDataURL('image/png');
  };

  const canSave = method === 'draw' ? !isEmpty : typedName.trim().length > 0;

  return (
    <Dialog open={true} onOpenChange={onCancel}>
      <DialogContent className="max-w-2xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Sign as {roleLabel}</DialogTitle>
          <DialogDescription>{signerName}</DialogDescription>
        </DialogHeader>

        <Tabs value={method} onValueChange={(v) => setMethod(v as 'draw' | 'type')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="draw" className="min-h-[44px]">
              <Pen className="w-4 h-4 mr-2" />
              Draw
            </TabsTrigger>
            <TabsTrigger value="type" className="min-h-[44px]">
              <Type className="w-4 h-4 mr-2" />
              Type
            </TabsTrigger>
          </TabsList>

          <TabsContent value="draw" className="space-y-4">
            <div 
              ref={containerRef}
              className="border-2 border-border rounded-lg overflow-hidden bg-background"
            >
              <SignatureCanvas
                ref={sigCanvas}
                canvasProps={{
                  width: canvasSize.width,
                  height: canvasSize.height,
                  className: 'signature-canvas touch-none',
                }}
                onEnd={handleDrawEnd}
              />
            </div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 text-sm text-muted-foreground">
              <span>Sign above with your finger or stylus</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleClear}
                disabled={isEmpty}
                className="min-h-[44px] w-full sm:w-auto"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Clear
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="type" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="typed-name">Type your full name</Label>
              <Input
                id="typed-name"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                placeholder="Enter your full name"
                className="text-2xl sm:text-4xl font-serif min-h-[56px]"
                style={{ fontFamily: '"Brush Script MT", cursive' }}
              />
            </div>
            <div className="text-sm text-muted-foreground">
              Your name will be converted to a signature
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button 
            type="button" 
            variant="outline" 
            onClick={onCancel} 
            disabled={isLoading}
            className="min-h-[44px] w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button 
            type="button" 
            onClick={handleSave} 
            disabled={!canSave || isLoading}
            className="min-h-[44px] w-full sm:w-auto"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              'Accept Signature'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
